import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import api from "../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import BoardingCard from "../components/BoardingCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import "../css/BoardingScreen.css";

export default function BoardingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAddedPackage = useRef(false);

  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  // === Fetch packages from backend ===
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);

        const res = await api.get("/boarding-packages/");
        const formatted = res.data.map((pkg) => ({
          id: pkg.id,
          title: pkg.package_name,
          subtitle: pkg.sub_title,
          subscribedPrice: `${pkg.subscribed_user_offer_amount}`,
          unsubscribedPrice: `${pkg.package_amount}`,
          tag: pkg.tags,
        }));

        setPackages(formatted);
      } catch (err) {
        console.error("Failed to fetch boarding packages:", err);
        // Safe toast with setTimeout
        setTimeout(() => {
          toast.error("Failed to load packages.");
        }, 0);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // === Add new package from AddPackage screen ===
  useEffect(() => {
    if (location.state?.newPackage && !hasAddedPackage.current) {
      setPackages((prev) => [...prev, location.state.newPackage]);
      hasAddedPackage.current = true;

      // Safe toast
      setTimeout(() => {
        toast.success("New package added successfully!");
      }, 0);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // === Delete package ===
  const handleDelete = (id) => {
    const pkg = packages.find((p) => p.id === id);
    setPackageToDelete(pkg);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!packageToDelete) return;
    try {
      await api.delete(`/boarding-packages/${packageToDelete.id}/`);
      setPackages((prev) =>
        prev.filter((pkg) => pkg.id !== packageToDelete.id)
      );

      setTimeout(() => {
        toast.success("Package deleted successfully!");
      }, 0);
    } catch (err) {
      console.error("Delete failed:", err);
      setTimeout(() => {
        toast.error("Failed to delete package.");
      }, 0);
    } finally {
      setIsDeleteModalOpen(false);
      setPackageToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPackageToDelete(null);
  };

  // === Edit package ===
  const handleEditClick = (pkg) => {
    setEditingPackage({ ...pkg });
  };

  const handleCloseEditModal = () => {
    setEditingPackage(null);
  };

  const handleSave = async (updatedPackage) => {
    try {
      setLoading(true);

      const endpoint = `/boarding-packages/${updatedPackage.id}/`;
      const body = {
        package_name: updatedPackage.title,
        subscribed_user_offer_amount: updatedPackage.subscribedPrice,
        package_amount: updatedPackage.unsubscribedPrice,
        sub_title: updatedPackage.subtitle,
      };

      await api.patch(endpoint, body);

      setPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === updatedPackage.id ? updatedPackage : pkg
        )
      );

      setTimeout(() => {
        toast.success("Package updated successfully!");
      }, 0);

      handleCloseEditModal();
    } catch (err) {
      console.error("Update failed:", err);
      setTimeout(() => {
        toast.error("Failed to update package.");
      }, 0);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />

        <div className="main-content">
          <div className="heading-sec">
            <PageTitle title="Boarding" onBack={handleBack} />
            <div className="add-package-sec">
              <button
                className="add-package-btn"
                onClick={() =>
                  navigate("/add_boarding", { state: { type: "Boarding" } })
                }
              >
                <IoMdAdd className="add-package-icon" color="#EBA8BF" /> Add
                Package
              </button>
            </div>
          </div>

          {loading && <p>Loading packages...</p>}

          <div className="boarding-cards-container-grid">
            {!loading && packages.length > 0 ? (
              packages.map((pkg) => (
                <BoardingCard
                  key={pkg.id}
                  item={pkg}
                  onAddService={() => handleEditClick(pkg)}
                  onDelete={() => handleDelete(pkg.id)}
                  subtitle={pkg.subtitle}
                />
              ))
            ) : (
              !loading && <p className="no-packages-text">No packages found.</p>
            )}
          </div>

          {editingPackage && (
            <div className="boarding-modal-overlay">
              <div className="boarding-modal-box">
                <BoardingCard
                  item={editingPackage}
                  isEditable={true}
                  tag={editingPackage.tag}
                  onClose={handleCloseEditModal}
                  onSave={handleSave}
                  subtitle={editingPackage.subtitle}
                />
              </div>
            </div>
          )}

          <DeleteWithConfirmation
            isOpen={isDeleteModalOpen}
            onCancel={closeDeleteModal}
            onConfirm={confirmDelete}
            title={`Delete ${packageToDelete?.title || "Package"}?`}
            message="Are you sure you want to delete this package? This action cannot be undone."
          />
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
